import { ContextError } from '@/errors';
import { useCallback, useContext } from 'react';

import { isContactFormDirtyInRocket } from '@/pages/ListDetailPage/rocketIntegration';
import { Context } from './Context';

/**
 * Hook that (assuming enabled) checks if the rocket contact form is dirty,
 * returning a promise that resolves to true if it is safe to proceed. A dialog
 * is displayed to use if the form is dirty, which allows them to acknowledge
 * they need to make changes or proceed anyway.
 *
 * Specifically:
 *
 * If isEnabled() has been never been called or has been called with false, no
 * dialog is shown and the promise resolves to true immediately.
 *
 * If it is not dirty, no dialog is shown and the promise resolves to true
 * immediately.
 *
 * If it is dirty, a dialog is shown and the promise resolves when the user
 * clicks one of two buttons.
 *
 * If they click 'OK': promise resolves to false, indicating they accept there
 * are unsaved changes. In this case it is expected the callee will not continue
 * with the navigation.
 *
 * If they click 'Proceed anyway': promise resolves to true, the same as if the
 * form were never dirty.
 */
export function useCheckRocketFormStatus() {
  const context = useContext(Context);

  if (context === null) {
    throw new ContextError(
      'Error: Tried to useCheckRocketFormStatus outside of a <RocketChangesGuardProvider>'
    );
  }

  const { setIsDialogOpen, resultResolverRef, isEnabled, setIsEnabled } = context;

  const checkRocketFormStatus = useCallback(async () => {
    if (!isEnabled) return true;

    if (await isContactFormDirtyInRocket()) {
      setIsDialogOpen(true);
      return new Promise<boolean>((resolve) => {
        resultResolverRef.current = resolve;
      });
    } else {
      setIsDialogOpen(false);
      return true;
    }
  }, [isEnabled, setIsDialogOpen, resultResolverRef]);

  return {
    checkRocketFormStatus,
    setIsEnabled,
    isEnabled,
  };
}
